import React, { useState } from 'react';
import {
  MessageSquare, Plus, MoreHorizontal, Settings, CheckCircle, AlertTriangle,
  Mail, Hash, Globe, ExternalLink, Shield, Eye, Users, Inbox, HelpCircle,
  Activity, ChevronRight, Power, PowerOff
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { cn } from '../../lib/utils';

const channelModes = [
  {
    id: 'disabled',
    name: 'Disabled',
    description: 'Channel is not active. No messages processed.',
    icon: PowerOff,
    color: 'text-slate-500',
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-200',
    risk: 'none'
  },
  {
    id: 'silent',
    name: 'Silent',
    description: 'Monitor only. Requests logged but no responses sent.',
    icon: Eye,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-200',
    risk: 'none',
    recommended: true
  },
  {
    id: 'helpdesk',
    name: 'Help Desk',
    description: 'Routes to human agents. AI provides suggestions only.',
    icon: HelpCircle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-200',
    risk: 'low'
  },
  {
    id: 'team_only',
    name: 'Team Only',
    description: 'AI handles team-specific requests. Other requests routed to humans.',
    icon: Users,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    borderColor: 'border-purple-200',
    risk: 'medium'
  },
  {
    id: 'inbox_only',
    name: 'Inbox Only',
    description: 'Full AI handling within the inbox. End-to-end automation.',
    icon: Inbox,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-200',
    risk: 'high'
  }
];

const channelTypes = {
  slack: { name: 'Slack', icon: Hash, color: 'text-[#4A154B]' },
  teams: { name: 'Teams', icon: MessageSquare, color: 'text-[#6264A7]' },
  email: { name: 'Email', icon: Mail, color: 'text-blue-600' },
  portal: { name: 'Portal', icon: Globe, color: 'text-green-600' }
};

const mockChannels = [
  {
    id: 'ch-1',
    name: '#it-help',
    type: 'slack',
    team: 'IT Operations',
    mode: 'helpdesk',
    status: 'active',
    stats: { messagesLast7d: 234, resolvedLast7d: 189, avgResponseTime: '4m' }
  },
  {
    id: 'ch-2',
    name: '#access-requests',
    type: 'slack',
    team: 'Identity & Access',
    mode: 'team_only',
    status: 'active',
    stats: { messagesLast7d: 156, resolvedLast7d: 142, avgResponseTime: '2m' }
  },
  {
    id: 'ch-3',
    name: 'it-support@company.com',
    type: 'email',
    team: 'IT Operations',
    mode: 'silent',
    status: 'active',
    stats: { messagesLast7d: 89, resolvedLast7d: 0, avgResponseTime: null }
  },
  {
    id: 'ch-4',
    name: '#security-alerts',
    type: 'slack',
    team: 'Security',
    mode: 'inbox_only',
    status: 'active',
    stats: { messagesLast7d: 67, resolvedLast7d: 63, avgResponseTime: '1m' }
  },
  {
    id: 'ch-5',
    name: 'IT Help Desk',
    type: 'teams',
    team: 'IT Operations',
    mode: 'disabled',
    status: 'inactive',
    stats: { messagesLast7d: 0, resolvedLast7d: 0, avgResponseTime: null }
  }
];

export const Channels = () => {
  const [channels, setChannels] = useState(mockChannels);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showModeDialog, setShowModeDialog] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);

  const handleModeChange = (channelId, newMode) => {
    const channel = channels.find(c => c.id === channelId);
    const mode = channelModes.find(m => m.id === newMode);

    if (mode.risk === 'high' || (channel.mode === 'disabled' && newMode !== 'silent')) {
      setSelectedChannel(channel);
      setPendingMode(newMode);
      setShowModeDialog(true);
    } else {
      applyModeChange(channelId, newMode);
    }
  };

  const applyModeChange = (channelId, newMode) => {
    setChannels(channels.map(ch =>
      ch.id === channelId
        ? { ...ch, mode: newMode, status: newMode === 'disabled' ? 'inactive' : 'active' }
        : ch
    ));
    setShowModeDialog(false);
    setSelectedChannel(null);
    setPendingMode(null);
  };

  const activeChannels = channels.filter(c => c.status === 'active');
  const modeStats = channelModes.map(mode => ({
    ...mode,
    count: channels.filter(c => c.mode === mode.id).length
  }));

  return (
    <div className="min-h-full bg-background">
      <div className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Channels</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Configure intake channels with safe rollout modes
              </p>
            </div>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Connect Channel
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-sm font-medium text-foreground mb-4">Channel Mode Distribution</h2>
          <div className="grid grid-cols-5 gap-4">
            {modeStats.map((mode) => {
              const Icon = mode.icon;
              return (
                <Card key={mode.id} className={cn("border", mode.borderColor)}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", mode.bgColor)}>
                        <Icon className={cn("w-5 h-5", mode.color)} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-foreground">{mode.count}</p>
                        <p className="text-xs text-muted-foreground">{mode.name}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Safe Rollout Recommendation</p>
                <p className="text-sm text-blue-700 mt-1">
                  Start new channels in <strong>Silent</strong> mode to observe request patterns without responding.
                  Graduate to <strong>Help Desk</strong> mode to provide AI suggestions to human agents.
                  Only enable <strong>Team Only</strong> or <strong>Inbox Only</strong> after validating workflows.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {channels.map((channel) => {
            const channelType = channelTypes[channel.type];
            const currentMode = channelModes.find(m => m.id === channel.mode);
            const ChannelIcon = channelType.icon;
            const ModeIcon = currentMode.icon;

            return (
              <Card key={channel.id} className="hover:border-primary/50 transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={cn("w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0")}>
                        <ChannelIcon className={cn("w-5 h-5", channelType.color)} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold text-foreground">{channel.name}</h3>
                          <Badge variant="outline" className="text-xs">{channelType.name}</Badge>
                          {channel.status === 'inactive' && (
                            <Badge variant="secondary" className="text-xs">Inactive</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5">Team: {channel.team}</p>

                        {channel.stats.messagesLast7d > 0 && (
                          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" />
                              {channel.stats.messagesLast7d} messages (7d)
                            </span>
                            {channel.stats.resolvedLast7d > 0 && (
                              <span className="flex items-center gap-1">
                                <CheckCircle className="w-3 h-3 text-green-500" />
                                {channel.stats.resolvedLast7d} resolved
                              </span>
                            )}
                            {channel.stats.avgResponseTime && (
                              <span className="flex items-center gap-1">
                                <Activity className="w-3 h-3" />
                                {channel.stats.avgResponseTime} avg response
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground mb-1">Channel Mode</p>
                        <Select
                          value={channel.mode}
                          onValueChange={(value) => handleModeChange(channel.id, value)}
                        >
                          <SelectTrigger className={cn("w-40", currentMode.borderColor)}>
                            <div className="flex items-center gap-2">
                              <ModeIcon className={cn("w-4 h-4", currentMode.color)} />
                              <SelectValue />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            {channelModes.map((mode) => {
                              const Icon = mode.icon;
                              return (
                                <SelectItem key={mode.id} value={mode.id}>
                                  <div className="flex items-center gap-2">
                                    <Icon className={cn("w-4 h-4", mode.color)} />
                                    <span>{mode.name}</span>
                                    {mode.recommended && (
                                      <Badge variant="secondary" className="text-[10px] ml-1">Recommended</Badge>
                                    )}
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Settings className="w-4 h-4 mr-2" />
                            Channel Settings
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Activity className="w-4 h-4 mr-2" />
                            View Activity
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Open in {channelType.name}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive">
                            <PowerOff className="w-4 h-4 mr-2" />
                            Disconnect
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-base">Channel Mode Reference</CardTitle>
            <CardDescription>Understanding the safe rollout ladder</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {channelModes.map((mode, idx) => {
                const Icon = mode.icon;
                return (
                  <div key={mode.id} className="flex items-start gap-4">
                    <div className="flex items-center gap-2 w-8">
                      {idx > 0 && (
                        <div className="w-px h-4 bg-border -mt-8 ml-4" />
                      )}
                    </div>
                    <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center shrink-0", mode.bgColor)}>
                      <Icon className={cn("w-5 h-5", mode.color)} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium text-foreground">{mode.name}</h4>
                        {mode.recommended && (
                          <Badge variant="secondary" className="text-xs">Start Here</Badge>
                        )}
                        {mode.risk !== 'none' && (
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-xs",
                              mode.risk === 'low' && "border-green-200 text-green-700",
                              mode.risk === 'medium' && "border-amber-200 text-amber-700",
                              mode.risk === 'high' && "border-red-200 text-red-700"
                            )}
                          >
                            {mode.risk} risk
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">{mode.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={showModeDialog} onOpenChange={setShowModeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Mode Change
            </DialogTitle>
            <DialogDescription>
              You're about to change the mode for <strong>{selectedChannel?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          {selectedChannel && pendingMode && (
            <div className="py-4">
              <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Current Mode</p>
                  <p className="text-sm font-medium text-foreground">
                    {channelModes.find(m => m.id === selectedChannel.mode)?.name}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">New Mode</p>
                  <p className="text-sm font-medium text-foreground">
                    {channelModes.find(m => m.id === pendingMode)?.name}
                  </p>
                </div>
              </div>

              {channelModes.find(m => m.id === pendingMode)?.risk === 'high' && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>Warning:</strong> Inbox Only mode enables full AI automation.
                    Ensure your workflows are tested and approved before enabling.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModeDialog(false)}>Cancel</Button>
            <Button onClick={() => applyModeChange(selectedChannel?.id, pendingMode)}>
              Confirm Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Channels;
